import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { MagneticButton } from '@/components/MagneticButton';
import { useScrolled } from '@/lib/hooks';
import { useSite } from '@/context/SiteContext';

const LINKS = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Services', to: '/services' },
  { label: 'Projects', to: '/projects', live: true },
  { label: 'Locations', to: '/locations' },
  { label: 'Clients', to: '/clients' },
  { label: 'Equipment', to: '/equipment' },
  { label: 'Contact', to: '/contact' },
];

const LiveBadge = ({ large = false }) => (
  <span
    data-testid='nav-live-badge'
    className={`relative inline-flex items-center gap-1 overflow-hidden rounded-full bg-[#16a34a] font-bold uppercase leading-none tracking-wider text-white shadow-[0_0_14px_rgba(34,197,94,0.55)] ${
      large ? 'ml-3 px-2.5 py-1 text-[11px]' : 'ml-2 px-2 py-[3px] text-[9px]'
    }`}
  >
    <span className='relative z-10 flex items-center gap-1'>
      <span className={`rounded-full bg-white motion-safe:animate-pulse ${large ? 'h-2 w-2' : 'h-1.5 w-1.5'}`} />
      LIVE
    </span>
    <span className='nav-live-sweep' aria-hidden='true' />
  </span>
);

export const Navbar = () => {
  const scrolled = useScrolled(60);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { site } = useSite();
  const phone = site?.phones?.[0];

  return (
    <>
      <header
        data-testid='site-nav'
        className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
          scrolled
            ? 'bg-[#20272A]/85 backdrop-blur-md border-b border-white/10 py-2'
            : 'bg-gradient-to-b from-black/40 to-transparent py-4'
        }`}
      >
        <div className='container-x flex items-center justify-between'>
          <BrandLogo height={scrolled ? 34 : 40} />

          <nav className='hidden xl:flex items-center gap-7' aria-label='Primary'>
            {LINKS.map((l) => (
              <div key={l.to} className='flex items-center'>
                <NavLink
                  to={l.to}
                  end={l.to === '/'}
                  data-cursor='GO'
                  className={({ isActive }) =>
                    `link-underline text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                      isActive ? 'text-[#B88A3B]' : 'text-[#F6F5F1]/85 hover:text-[#F6F5F1]'
                    }`
                  }
                >
                  {l.label}
                </NavLink>
                {l.live && <LiveBadge />}
              </div>
            ))}
          </nav>

          <div className='flex items-center gap-3'>
            {phone && (
              <a
                href={`tel:${phone.replace(/\s/g, '')}`}
                className='hidden md:inline-flex items-center gap-2 text-[13px] text-[#F6F5F1]/80 hover:text-[#B88A3B] transition-colors'
                data-testid='nav-phone'
              >
                <Phone size={15} /> {phone}
              </a>
            )}
            <div className='hidden sm:block'>
              <MagneticButton to='/contact' variant='gold' data-testid='nav-primary-cta-button' data-cursor='ENQUIRE'>
                Start a Project
              </MagneticButton>
            </div>
            <button
              className='xl:hidden inline-flex items-center justify-center h-11 w-11 rounded-lg border border-white/15 text-[#F6F5F1]'
              onClick={() => setOpen(true)}
              aria-label='Open menu'
              data-testid='nav-mobile-open-button'
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className='fixed inset-0 z-50 bg-[#20272A]'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid='mobile-menu'
          >
            <div className='grain absolute inset-0' />
            <div className='relative container-x flex items-center justify-between py-4'>
              <BrandLogo height={38} />
              <button
                className='inline-flex items-center justify-center h-11 w-11 rounded-lg border border-white/15 text-[#F6F5F1]'
                onClick={() => setOpen(false)}
                aria-label='Close menu'
                data-testid='nav-mobile-close-button'
              >
                <X size={20} />
              </button>
            </div>
            <nav className='relative container-x mt-8 flex flex-col' aria-label='Mobile'>
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === '/'}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block border-b border-white/10 py-4 font-head text-3xl sm:text-4xl tracking-tight ${
                        isActive ? 'text-[#B88A3B]' : 'text-[#F6F5F1]'
                      }`
                    }
                  >
                    <span className='eyebrow text-white/30 mr-3 align-middle'>{String(i + 1).padStart(2, '0')}</span>
                    {l.label}
                    {l.live && <LiveBadge large />}
                  </NavLink>
                </motion.div>
              ))}
              <div className='mt-8'>
                <MagneticButton to='/contact' variant='gold' onClick={() => setOpen(false)} className='w-full'>
                  Start a Project
                </MagneticButton>
              </div>
              {phone && (
                <a href={`tel:${phone.replace(/\s/g, '')}`} className='mt-6 inline-flex items-center gap-2 text-[#F6F5F1]/80'>
                  <Phone size={16} /> {phone}
                </a>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
